import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InviteCreated, InviteRole } from "@websede/contracts";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { Button, Card, ErrorNotice, Field, Input, Select } from "../../components/form.js";

const ROLES: InviteRole[] = ["user", "admin", "billing", "group_manager"];
const INVITE_TTL_DAYS = 7;

/** Invite form plus pending invitations; rendered inside the console's Users page. */
export function InvitationCards() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("user");
  const [last, setLast] = useState<InviteCreated | null>(null);

  const invites = useQuery({ queryKey: ["invites", "list"], queryFn: () => api.invites.list() });
  const onCreated = async (result: InviteCreated) => {
    setLast(result);
    await queryClient.invalidateQueries({ queryKey: ["invites", "list"] });
  };
  const create = useMutation({
    mutationFn: () => api.invites.create({ email, role }),
    onSuccess: async (result) => {
      setEmail("");
      await onCreated(result);
    },
  });
  const resend = useMutation({
    mutationFn: (id: string) => api.invites.resend(id),
    onSuccess: onCreated,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.invites.revoke(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["invites", "list"] }),
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };
  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso));

  return (
    <>
      <Card title={t("settings.team.inviteTitle")}>
        <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Field label={t("auth.fields.email")}>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
          </div>
          <div className="sm:w-48">
            <Field label={t("settings.team.role")}>
              <Select value={role} onChange={(e) => setRole(e.target.value as InviteRole)}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(`roles.${r}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Button type="submit" disabled={create.isPending}>
            {t("settings.team.invite")}
          </Button>
        </form>
        <ErrorNotice error={create.error ?? resend.error} />
        {last ? <InviteResult result={last} /> : null}
      </Card>

      <Card title={t("settings.team.pendingTitle")}>
        <ErrorNotice error={invites.error ?? revoke.error} />
        {invites.data && invites.data.invites.length === 0 ? (
          <p className="text-sm text-ink-3">{t("settings.team.none")}</p>
        ) : null}
        <ul className="divide-y divide-rule">
          {(invites.data?.invites ?? []).map((invite) => (
            <li key={invite.id} className="flex items-center justify-between gap-4 py-3 text-sm">
              <div className="min-w-0">
                <div className="truncate">{invite.email}</div>
                <div className="text-xs text-ink-3">
                  {t(`roles.${invite.role}`)} ·{" "}
                  {t("settings.team.invitedBy", { name: invite.invitedBy.displayName })} ·{" "}
                  {t("settings.team.expires", { date: format(invite.expiresAt) })}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  variant="secondary"
                  onClick={() => resend.mutate(invite.id)}
                  disabled={resend.isPending}
                >
                  {t("settings.team.resend")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => revoke.mutate(invite.id)}
                  disabled={revoke.isPending}
                >
                  {t("settings.team.revoke")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

/** Outcome of the last invitation: emailed, or a link to pass on when email failed. */
function InviteResult({ result }: { result: InviteCreated }) {
  const { t } = useTranslation();
  const [showLink, setShowLink] = useState(!result.emailSent);
  const [copied, setCopied] = useState(false);
  const vars = { email: result.invite.email, days: INVITE_TTL_DAYS };
  const tone = result.emailSent
    ? "border-good bg-good-soft text-good"
    : "border-warn bg-warn-soft text-warn";

  return (
    <div className={`rounded-md border p-4 text-sm ${tone}`}>
      <p className="font-medium">
        {result.emailSent ? t("settings.team.sent", vars) : t("settings.team.notSent", vars)}
      </p>
      <p className="mt-1 text-xs text-ink-2">
        {result.emailSent ? t("settings.team.sentHint", vars) : null}{" "}
        {result.emailSent ? (
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => setShowLink((v) => !v)}
          >
            {showLink ? t("settings.team.hideLink") : t("settings.team.showLink")}
          </button>
        ) : null}
      </p>
      {showLink ? (
        <div className="mt-2 flex items-center gap-2">
          <Input
            readOnly
            value={result.acceptUrl}
            className="font-mono text-xs"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            variant="secondary"
            onClick={async () => {
              await navigator.clipboard.writeText(result.acceptUrl);
              setCopied(true);
            }}
          >
            {copied ? t("common.copied") : t("common.copy")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
